import React from 'react';
import PropTypes from 'prop-types';
import { formats } from 'utils';
import {
  isH3TXPolicy,
  isSCIPXPolicy,
  isSCVioletPolicy,
} from 'utils/policyType';

import Typography from '../Typography';

const baseDeductibleSkipList = [
  'Hurricane Percentage',
  'Named Storm Deductible Percentage',
  'All Other Perils Percentage',
];

const deductibleTitleRename = {
  'Hurricane Amount': 'Hurricane',
  'Named Storm Deductible Amount': 'Named Storm',
  'All Other Perils Amount': 'All Other Perils',
};

const isExcludedish = (v) =>
  v === 0 || v === '0' || v === 'Excluded' || v === '' || v == null;

const skipItem = (title, item, policyNumber, deductibleSkipList) => {
  if (
    title === 'Coverages' &&
    item.title !== 'Coverage C - Personal Property' &&
    item.value === 0
  ) {
    return true;
  }

  if (title === 'Deductibles') {
    if (
      deductibleSkipList.includes(item.title) ||
      (item.title === 'Hurricane Amount' && isSCIPXPolicy(policyNumber)) ||
      (item.title === 'Earthquake' &&
        isSCIPXPolicy(policyNumber) &&
        item.value === 0) ||
      item.value === ''
    ) {
      return true;
    }
  }

  return false;
};

const List = ({ title, data, style, policySystem, policyNumber }) => {
  const deductibleSkipList = [...baseDeductibleSkipList];

  const getDeductibleValue = (item) => {
    let value =
      typeof item?.value === 'number'
        ? formats.moneyWithoutDecimalsFormat(item.value)
        : item?.value;

    if (policySystem === 'Violet') {
      if (isH3TXPolicy(policyNumber) && title === 'Deductibles') {
        const hurricaneItem = data.find((i) => i.title.includes('Hurricane'));
        const windHailItem = data.find((i) => i.title.includes('Wind/Hail'));

        if (
          isExcludedish(hurricaneItem?.value) ||
          isExcludedish(windHailItem?.value)
        ) {
          deductibleSkipList.push('Hurricane');
          deductibleSkipList.push('Wind/Hail');
        }
      }

      if (isSCVioletPolicy(policyNumber)) {
        if (
          title !== 'Deductibles' &&
          item.title === 'Coverage C - Personal Property' &&
          (item.value === 0 || item.value === 'Excluded')
        ) {
          return { title: item.title, value: 'Excluded' };
        }

        if (
          item.title === 'Named Storm Deductible' ||
          item.title === 'Hurricane Amount'
        ) {
          const namedStormItem = data.find((i) =>
            i.title.includes('Named Storm Deductible'),
          );

          const windHailItem = data.find((i) =>
            i.title.includes('Hurricane Amount'),
          );

          if (windHailItem && !namedStormItem) {
            return {
              title: 'Wind/Hail',
              value:
                windHailItem?.value > 0
                  ? formats.moneyWithoutDecimalsFormat(windHailItem?.value)
                  : 'Excluded',
            };
          }

          if (namedStormItem && windHailItem) {
            if (
              isExcludedish(namedStormItem.value) &&
              isExcludedish(windHailItem.value)
            ) {
              deductibleSkipList.push('Named Storm Deductible');

              return {
                title: 'Wind/Hail',
                value: 'Excluded',
              };
            }

            if (
              namedStormItem?.value > windHailItem?.value ||
              (namedStormItem?.value > 0 && isExcludedish(windHailItem?.value))
            ) {
              deductibleSkipList.push('Hurricane Amount');

              return {
                title: 'Named Storm',
                value: formats.moneyWithoutDecimalsFormat(namedStormItem.value),
              };
            }

            if (
              windHailItem?.value > namedStormItem?.value ||
              (windHailItem?.value > 0 && isExcludedish(namedStormItem?.value))
            ) {
              deductibleSkipList.push('Named Storm Deductible');

              return {
                title: 'Wind/Hail',
                value: formats.moneyWithoutDecimalsFormat(windHailItem?.value),
              };
            }
          }
        }

        if (item.title === 'All Other Perils') {
          return {
            title: 'All Other Perils',
            value: formats.moneyWithoutDecimalsFormat(item.value),
          };
        }
      } else {
        deductibleSkipList.push('Named Storm Deductible');
      }
    }

    if (policySystem === 'IPX') {
      if (
        item.title === 'Named Storm Deductible Amount' ||
        item.title === 'Hurricane Amount'
      ) {
        const namedStormItem = data.find((i) =>
          i.title.includes('Named Storm Deductible'),
        );

        const windHailItem = data.find((i) =>
          i.title.includes('Hurricane Amount'),
        );

        if (
          namedStormItem &&
          windHailItem &&
          isExcludedish(namedStormItem.value) &&
          isExcludedish(windHailItem.value)
        ) {
          deductibleSkipList.push('Named Storm Deductible Amount');
          return {
            title: 'Wind/Hail',
            value: 'Excluded',
          };
        }
      }
    }

    const displayTitle = deductibleTitleRename[item.title] ?? item.title;

    if (title === 'Deductibles' && isExcludedish(item.value)) {
      return { title: displayTitle, value: 'Excluded' };
    }

    return { title: displayTitle, value };
  };

  const filterData = [];
  for (const item of data) {
    if (skipItem(title, item, policyNumber, deductibleSkipList)) {
      continue;
    }
    filterData.push(getDeductibleValue(item));
  }

  return (
    <div className="grid gap-[8px]" style={style}>
      <Typography type="caption1" component="span">
        {title}
      </Typography>

      {filterData.map((item, index) => (
        <div className="flex justify-between" key={index}>
          <Typography type="caption2" component="span">
            {item?.title}
          </Typography>

          <Typography
            id={item?.title}
            type="caption2"
            component="span"
            wordNoWrap
          >
            {item?.value}
          </Typography>
        </div>
      ))}
    </div>
  );
};

List.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ).isRequired,
  style: PropTypes.object,
  policySystem: PropTypes.string,
  policyNumber: PropTypes.string,
};

export default List;
